import { FileText } from "lucide-react";
import { Input, Label } from "@/components/ui/form";
import { SectionAddButton } from "@/components/SectionAddButton";
import { openAttachmentView, openHtmlDocument } from "@/utils/attachments";

export interface RfqAttachment {
  id: string;
  file_name: string;
  content_type?: string;
  source?: string;
  stage?: string;
  created_at?: string;
  entity_type?: string;
  entity_id?: string;
}

function DocumentLink({
  label,
  sublabel,
  onClick,
}: {
  label: string;
  sublabel?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start gap-2 w-full text-left rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 hover:border-argon-primary/30 hover:bg-argon-primary/5 transition-colors"
    >
      <FileText className="h-4 w-4 shrink-0 text-argon-primary mt-0.5" />
      <span>
        <span className="block text-sm font-semibold text-argon-primary hover:underline">{label}</span>
        {sublabel && <span className="block text-xs text-argon-secondary mt-0.5">{sublabel}</span>}
      </span>
    </button>
  );
}

export function RfqDocumentsSection({
  rfqId,
  rfqNumber,
  attachments,
  token,
  canEdit,
  showUpload,
  onUpload,
}: {
  rfqId: string;
  rfqNumber: string;
  attachments: RfqAttachment[];
  token: string | null;
  canEdit?: boolean;
  showUpload?: boolean;
  onUpload?: (file: File) => Promise<void>;
}) {
  const generated = attachments.filter((a) => a.source === "generated" && a.entity_type === "rfq");
  const uploaded = attachments.filter(
    (a) => a.source !== "generated" && a.entity_type === "rfq",
  );

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div className="space-y-2">
        <p className="text-xs font-semibold text-argon-dark">Generated</p>
        <p className="text-xs text-argon-secondary mb-2">
          A new seller quote version is saved automatically whenever the RFQ changes.
        </p>
        <DocumentLink
          label="Current seller quote"
          sublabel={`Live preview · ${rfqNumber}`}
          onClick={() => void openHtmlDocument(`/rfqs/${rfqId}/seller-quote/print`, token)}
        />
        {generated.length === 0 ? (
          <p className="text-xs text-argon-secondary">No saved versions yet.</p>
        ) : (
          generated.map((a) => (
            <DocumentLink
              key={a.id}
              label={a.file_name}
              sublabel={a.created_at ? `Saved ${a.created_at}` : "Saved version"}
              onClick={() =>
                void openAttachmentView(a.id, a.file_name, a.content_type ?? "text/html", token)
              }
            />
          ))
        )}
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold text-argon-dark">Uploaded</p>
        {showUpload && canEdit && onUpload && (
          <div className="rounded-lg border border-gray-100 p-3 space-y-2 bg-gray-50/40 mb-2">
            <Label>Upload file</Label>
            <Input
              type="file"
              className="max-w-sm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  void onUpload(file).finally(() => {
                    e.target.value = "";
                  });
                }
              }}
            />
          </div>
        )}
        {uploaded.length === 0 ? (
          <p className="text-xs text-argon-secondary">No uploaded documents.</p>
        ) : (
          uploaded.map((a) => (
            <DocumentLink
              key={a.id}
              label={a.file_name}
              sublabel={[a.stage, a.created_at].filter(Boolean).join(" · ") || undefined}
              onClick={() =>
                void openAttachmentView(a.id, a.file_name, a.content_type ?? "", token)
              }
            />
          ))
        )}
      </div>
    </div>
  );
}

export function RfqDocumentsAddButton({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) {
  return <SectionAddButton open={open} onClick={onClick} />;
}
